/**
 * Convertisseur Tiptap JSON → Content pdfmake
 *
 * Gère les nodes : doc, paragraph, heading, bulletList, orderedList, listItem, text
 * Gère les marks : bold, italic, underline, color (via textStyle)
 * Gère les attrs : textAlign, heading level
 */

import type { Content, ContentText } from "pdfmake/interfaces";
import { PDF_FONT_SIZES, PDF_COLORS, CORROSION_COLORS } from "./pdf-styles";
import { resolveVariables, type RapportVariables } from "./pdf-variables";
import { getCodeCouleur } from "@/modules/corrosion/calculs";
import type { ReseauStatGroup } from "./network-material-stats";

// Type Tiptap JSONContent (simplifié, compatible @tiptap/core)
interface TiptapNode {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  marks?: TiptapMark[];
  text?: string;
}

interface TiptapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

// ─── API publique ────────────────────────────────────────────

// Variables actives pour la session de conversion en cours (passées via tiptapToPdfmake)
let _vars: RapportVariables | undefined;
// Stats réseau x matériau actives (pour la variable {{mesures_min_max_moy}})
let _stats: ReseauStatGroup[] | undefined;

/**
 * Convertit un document Tiptap JSON en tableau de Content pdfmake.
 * Si `variables` est fourni, les occurrences {{xxx}} dans les noeuds texte
 * sont remplacées par leur valeur calculée.
 * Retourne un tableau vide si le document est vide ou null.
 */
export function tiptapToPdfmake(
  doc: unknown,
  variables?: RapportVariables,
  stats?: ReseauStatGroup[]
): Content[] {
  if (!doc || typeof doc !== "object") return [];
  const node = doc as TiptapNode;
  if (node.type !== "doc" || !node.content) return [];

  _vars = variables;
  _stats = stats;
  const result: Content[] = [];
  for (const child of node.content) {
    const converted = convertNode(child);
    if (converted !== null) {
      result.push(converted);
    }
  }
  _vars = undefined;
  return result;
}

// ─── Conversion récursive ────────────────────────────────────

function convertNode(node: TiptapNode): Content | null {
  switch (node.type) {
    case "paragraph":
      return convertParagraph(node);
    case "heading":
      return convertHeading(node);
    case "bulletList":
      return convertBulletList(node);
    case "orderedList":
      return convertOrderedList(node);
    case "horizontalRule":
      return {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: "#CCCCCC" }],
        margin: [0, 6, 0, 6] as [number, number, number, number],
      };
    case "blockquote":
      return convertBlockquote(node);
    default:
      return null;
  }
}

function convertParagraph(node: TiptapNode): Content {
  const inlineContent = convertInlineContent(node.content);
  const alignment = getAlignment(node.attrs);

  // Paragraphe vide (Enter ×2) → une ligne blanche au même interligne + 3pt
  if (inlineContent.length === 0) {
    return {
      text: " ",
      fontSize: PDF_FONT_SIZES.body,
      lineHeight: 1.2,
      margin: [0, 0, 0, 24] as [number, number, number, number],
    };
  }

  // Enter = +3pt entre paragraphes ; Shift+Enter (hardBreak) = 0 (géré dans convertInlineContent)
  return {
    text: inlineContent,
    fontSize: PDF_FONT_SIZES.body,
    color: PDF_COLORS.text,
    lineHeight: 1.2,
    alignment,
    margin: [0, 0, 0, 12] as [number, number, number, number],
  };
}

function convertHeading(node: TiptapNode): Content {
  const level = (node.attrs?.level as number) ?? 1;
  const inlineContent = convertInlineContent(node.content);
  const alignment = getAlignment(node.attrs);

  const styleMap: Record<number, string> = { 1: "heading1", 2: "heading2", 3: "heading3" };

  return {
    text: inlineContent,
    style: styleMap[level] ?? "heading1",
    alignment,
  };
}

function convertBulletList(node: TiptapNode): Content {
  const items = (node.content ?? []).map(convertListItem).filter(Boolean) as Content[];
  return {
    ul: items,
    fontSize: PDF_FONT_SIZES.body,
    color: PDF_COLORS.text,
    margin: [0, 2, 0, 4] as [number, number, number, number],
  };
}

function convertOrderedList(node: TiptapNode): Content {
  const items = (node.content ?? []).map(convertListItem).filter(Boolean) as Content[];
  return {
    ol: items,
    fontSize: PDF_FONT_SIZES.body,
    color: PDF_COLORS.text,
    margin: [0, 2, 0, 4] as [number, number, number, number],
  };
}

function convertListItem(node: TiptapNode): Content | null {
  if (node.type !== "listItem" || !node.content) return null;

  // Un listItem contient souvent un seul paragraph → extraire son contenu inline
  const parts: Content[] = [];
  for (const child of node.content) {
    if (child.type === "paragraph") {
      const inline = convertInlineContent(child.content);
      if (inline.length > 0) {
        parts.push({ text: inline, lineHeight: 1.3 });
      }
    } else {
      const converted = convertNode(child);
      if (converted) parts.push(converted);
    }
  }

  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return { stack: parts };
}

function convertBlockquote(node: TiptapNode): Content {
  const innerContent: Content[] = [];
  for (const child of node.content ?? []) {
    const converted = convertNode(child);
    if (converted) innerContent.push(converted);
  }

  return {
    stack: innerContent,
    margin: [10, 4, 0, 4] as [number, number, number, number],
    border: [true, false, false, false] as [boolean, boolean, boolean, boolean],
    borderColor: [PDF_COLORS.grayBorder, "", "", ""],
  } as Content;
}

// ─── Inline content (text + marks) ───────────────────────────

function convertInlineContent(children?: TiptapNode[]): ContentText[] {
  if (!children) return [];

  const result: ContentText[] = [];
  for (const child of children) {
    if (child.type === "text" && child.text) {
      result.push(convertTextNode(child));
    } else if (child.type === "hardBreak") {
      result.push({ text: "\n" });
    }
  }
  return result;
}

function convertTextNode(node: TiptapNode): ContentText {
  const rawText = node.text ?? "";

  // Variable rédactionnelle {{mesures_min_max_moy}} : se déplie en prose
  // (une phrase par couple réseau x matériau). Traitée à part car
  // resolveVariables ne produit qu'une valeur scalaire.
  if (_stats && rawText.includes(STATS_PLACEHOLDER)) {
    const runs: Content[] = [];
    const parts = rawText.split(STATS_PLACEHOLDER);
    parts.forEach((part, i) => {
      if (part) {
        runs.push({ text: _vars ? resolveVariables(part, _vars) : part });
      }
      if (i < parts.length - 1) {
        runs.push(...buildStatsRuns(_stats!));
      }
    });
    return { text: runs };
  }

  const resolvedText = _vars ? resolveVariables(rawText, _vars) : rawText;
  const textObj: ContentText = { text: resolvedText };

  if (!node.marks) return textObj;

  for (const mark of node.marks) {
    switch (mark.type) {
      case "bold":
        textObj.bold = true;
        break;
      case "italic":
        textObj.italics = true;
        break;
      case "underline":
        textObj.decoration = "underline";
        break;
      case "color":
        if (mark.attrs?.color) {
          textObj.color = mark.attrs.color as string;
        }
        break;
      case "textStyle":
        // textStyle peut porter la couleur directement
        if (mark.attrs?.color) {
          textObj.color = mark.attrs.color as string;
        }
        break;
    }
  }

  return textObj;
}

// ─── Helpers ─────────────────────────────────────────────────

// ─── Variable statistiques {{mesures_min_max_moy}} ───────────

const STATS_PLACEHOLDER = "{{mesures_min_max_moy}}";

/** Pourcentage au format français : virgule décimale + espace INSÉCABLE avant le %
 *  (U+00A0) pour que le nombre et le % ne soient jamais coupés par un retour à la ligne. */
function fmtPct(val: number): string {
  const n = parseFloat(val.toFixed(1));
  return `${n.toString().replace(".", ",")} %`;
}

/** Couleur hex (code couleur Renaud) pour un pourcentage de corrosion. */
function corrosionColorFor(pct: number): string {
  const code = getCodeCouleur(pct);
  return (code ? CORROSION_COLORS[code] : undefined) ?? PDF_COLORS.text;
}

/**
 * Qualificatif d'hétérogénéité selon l'écart max - min (en points de %), calé
 * sur les bandes du code couleur (~10 points par bande). Choix validé Renaud
 * (juin 2026). Règle : on ne chiffre JAMAIS l'écart dans le texte, seul le
 * qualificatif varie.
 */
function heterogeneiteQualif(spread: number): string {
  if (spread >= 25) return "très hétérogènes";
  if (spread >= 10) return "hétérogènes";
  return "relativement homogènes";
}

/**
 * Rend la variable {{mesures_min_max_moy}} en PROSE : une phrase par couple
 * réseau x matériau (modèle rédactionnel validé par Renaud, juin 2026), avec
 * min / max / moyenne en gras et colorés selon le code couleur (réseau en gras
 * aussi). Adapte la formulation :
 * une seule mesure → singulier ; valeur uniforme → « homogène » ; sinon
 * qualificatif d'hétérogénéité selon l'écart. Message neutre si aucune mesure.
 */
function buildStatsRuns(groups: ReseauStatGroup[]): Content[] {
  if (groups.length === 0) {
    return [
      {
        text: "Aucune mesure exploitable pour le calcul des statistiques.",
        italics: true,
        color: PDF_COLORS.textLight,
      },
    ];
  }
  const runs: Content[] = [];
  let first = true;
  for (const group of groups) {
    for (const mat of group.materials) {
      if (!first) runs.push({ text: "\n\n" });
      first = false;
      // « en acier », « en cuivre »... (omis si matériau non précisé)
      const enMateriau =
        mat.materiau && mat.materiau !== "Matériau non précisé"
          ? ` en ${mat.materiau.toLowerCase()}`
          : "";

      if (mat.nb === 1) {
        runs.push({ text: "La mesure d'épaisseur par ultrasons effectuée sur le réseau " });
        runs.push({ text: group.reseau, bold: true });
        runs.push({ text: `${enMateriau} montre une perte d'épaisseur de ` });
        runs.push({ text: fmtPct(mat.moyenne), color: corrosionColorFor(mat.moyenne), bold: true });
        runs.push({ text: ", sur la portion de réseau examinée dans le présent rapport." });
      } else if (mat.min === mat.max) {
        runs.push({ text: "Les mesures d'épaisseurs par ultrasons effectuées sur le réseau " });
        runs.push({ text: group.reseau, bold: true });
        runs.push({ text: `${enMateriau} montrent une perte d'épaisseur homogène de ` });
        runs.push({ text: fmtPct(mat.moyenne), color: corrosionColorFor(mat.moyenne), bold: true });
        runs.push({ text: ", sur les portions de réseau examinées dans le présent rapport." });
      } else {
        runs.push({ text: "Les mesures d'épaisseurs par ultrasons effectuées sur le réseau " });
        runs.push({ text: group.reseau, bold: true });
        runs.push({ text: `${enMateriau}, montrent des pertes d'épaisseur ${heterogeneiteQualif(mat.max - mat.min)} allant de ` });
        runs.push({ text: fmtPct(mat.min), color: corrosionColorFor(mat.min), bold: true });
        runs.push({ text: " de perte pour la plus basse à " });
        runs.push({ text: fmtPct(mat.max), color: corrosionColorFor(mat.max), bold: true });
        runs.push({ text: " de perte d'épaisseur pour la plus forte, sur les portions de réseau examinées dans le présent rapport. Moyenne de perte d'épaisseur d'environ " });
        runs.push({ text: fmtPct(mat.moyenne), color: corrosionColorFor(mat.moyenne), bold: true });
        runs.push({ text: "." });
      }
    }
  }
  return runs;
}

function getAlignment(attrs?: Record<string, unknown>): "left" | "center" | "right" | "justify" | undefined {
  const align = attrs?.textAlign as string | undefined;
  if (align === "center" || align === "right" || align === "justify") return align;
  return undefined; // left par défaut dans pdfmake
}
